import { Image } from "@/components/Image";
import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { cn } from "@/utils/helpers";
import { usePlayback } from "./mediaPlayback";

/**
 * The plate is the media surface of a wall card, and the same surface backs the
 * thumbnails and stages that open out of one.
 *
 * It knows nothing about what it is showing. It was lifted out of
 * trends/components/ when References needed the same three gates and the same
 * four-step degrade; nothing in it has ever mentioned a trend, and nothing in it
 * may start to. Callers hand it URLs.
 *
 * Three outcomes, and only three:
 *
 *  1. There is a mirrored video  → render it, and play it only when playback is
 *     allowed, the caller says it is being looked at, and the plate is actually
 *     on screen (see mediaPlayback.tsx). The one plate the viewer asked to hear
 *     keeps playing without the last two, because they chose to listen to it.
 *  2. There is only the still it was harvested with → show the still.
 *  3. There is neither, or the mirror is flagged broken → say so, in words, on a
 *     flat hatched surface that cannot be mistaken for footage.
 *
 * `standIn` is the fixture harness's escape hatch and nothing else: with the API
 * unreachable there is no media to show, and a wall of "no preview captured" is
 * a wall that cannot be judged. It is only ever consulted when the item HAS an
 * unbroken preview whose URLs did not survive the trip, so it can never dress up
 * outcome 3 as outcome 1. Production passes nothing.
 *
 * The two faces the plate draws for ITSELF are styled by components/wall.css,
 * which every surface using this component imports. The lit recipes a `standIn`
 * names are styled by the caller's own stylesheet, because the caller owns that
 * vocabulary.
 */

export type PlateGlow = "strategy" | "culture" | "creative" | "neutral";
export type PlateRecipe = "split" | "bars" | "arc" | "stack" | "grid" | "wedge";

export interface PlateArt {
  glow: PlateGlow;
  recipe: PlateRecipe;
}

/** How big the surface is, which is the only thing that changes between a wall
 *  plate and a carousel stage: the same words do not fit both, and the sentence
 *  a stage can honestly print is not the sentence a plate can. */
export type PlateScale = "plate" | "stage";

function artClass(art: PlateArt): string {
  return `tp-g-${art.glow} tp-${art.recipe}`;
}

function NoPreviewPlate({ hint }: { hint?: string }) {
  return (
    /* Plate empty (library, style B): the plate's own ground, the label in a
       radius 6 box and the reason under it, centred. The label sits in the
       frame's row of the card, not over its words, so centring is safe. */
    <div className="co-plate-surface co-plate-art--none">
      {/* .co-plate-surface is a block (it swaps with a video or an image), so
          the centring lives on an inner layer. */}
      <div className="flex h-full flex-col items-center justify-center gap-2.5 p-6">
      <span className="tt-label inline-flex h-6 items-center rounded-tag border border-co-line2 px-2.5 uppercase text-co-muted">
        No preview captured
      </span>
      {/* The second line is the CALLER's, because what opening the item reveals
          is a fact about that surface's domain and not about this box. Trends
          can promise the harvest it was built from; a reference was not built
          from anything, it IS the thing, so it promises its source instead.
          Absent, only the true half is printed. */}
      {hint ? <span className="w-[180px] max-w-full text-center text-[12px] leading-[1.55] text-co-faint">{hint}</span> : null}
      </div>
    </div>
  );
}

/**
 * The carousel stage face.
 *
 * A slide that cannot play is still a slide, so it says what it is instead of
 * going blank. The wall plate's version promises "open it to see what it was
 * built from", which is the wrong sentence here: the panel IS the thing that was
 * opened, and this submission is what it was built from. It states only what is
 * known, which is that nothing playable came back for this one.
 */
function NoPreviewStage() {
  return (
    <div className="co-plate-surface co-plate-art--none">
      <div className="flex h-full flex-col items-center justify-center gap-2.5 p-6">
        <span className="tt-label inline-flex h-6 items-center rounded-tag border border-co-line2 px-2.5 uppercase text-co-muted">
          No preview captured
        </span>
        <span className="w-[220px] max-w-full text-center text-[12px] leading-[1.55] text-co-faint">
          Nothing playable came back for this submission.
        </span>
      </div>
    </div>
  );
}

function nothingCaptured(scale: PlateScale, hint?: string) {
  if (scale === "stage") return <NoPreviewStage />;
  return <NoPreviewPlate hint={hint} />;
}

/**
 * The sound chip: bottom-left on the media, in media glass, and only on a wall
 * plate that has a video to hear. Its name is fixed, "Sound for {title}", and
 * aria-pressed says whether it is on: a name that flipped between "on" and
 * "off" beside a pressed state would say the state twice and could contradict
 * it. The title stays in the name because a wall carries many of these.
 *
 * Whether it is visible at rest is the wall's call, not the plate's, so its
 * resting visibility lives in the caller's stylesheet.
 */
function SoundChip({
  on,
  title,
  onToggle,
}: {
  on: boolean;
  title: string;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      className="co-media-chip co-sound-chip"
      onClick={onToggle}
      aria-pressed={on}
      aria-label={`Sound for ${title}`}
    >
      {on ? (
        <Volume2 className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
      ) : (
        <VolumeX className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
      )}
    </button>
  );
}

function PlateVideo({
  src,
  poster,
  active,
  withSound,
  keepPlaying = false,
  viewerPaused,
  plateId,
  plateTitle,
  restAsStill = false,
  onError,
  onNaturalSize,
}: {
  src: string;
  poster: string | null;
  /** Whether this surface is the one being looked at. A carousel shows one item
   *  at a time, so every slide but that one is held on its still even though all
   *  of them are mounted. The wall passes true: there every plate on screen is
   *  being looked at. */
  active: boolean;
  /** A detail stage passes true while the viewer has its sound on; a wall plate
   *  passes true while it is the one plate the viewer asked to hear and the wall
   *  is playing. A muted attribute is also what lets the plate autoplay at all,
   *  so nothing here unmutes itself without a click behind it. */
  withSound: boolean;
  /** This is the one wall plate being heard. It plays with sound until the
   *  viewer turns it off, pauses the wall, gives the sound to another plate,
   *  opens a detail or scrolls it wholly out of view, so `active` is not asked
   *  of it, and neither is the pre-roll margin the play gate uses. */
  keepPlaying?: boolean;
  /** The viewer's own Pause, on a surface that carries one (see PlateMedia). */
  viewerPaused?: boolean;
  /** Present only on a wall plate that may carry a sound chip. */
  plateId?: string;
  /** What the sound chip names in its label: "Sound for {title}". */
  plateTitle?: string;
  /** A wall plate: at rest it is only its poster, an <img>, and the <video> is
   *  built when it is asked to move. A wall of idle <video> elements is what
   *  made Trends crawl on slower machines, above all while the agent dock
   *  slides and every plate is laid out again each frame. */
  restAsStill?: boolean;
  onError: () => void;
  onNaturalSize?: (width: number, height: number) => void;
}) {
  const { allowed, reduced, paused, soundPlateId, setSoundPlate } = usePlayback();
  const ref = useRef<HTMLVideoElement | null>(null);
  /* The element as state too, so the effects below re-run when a resting plate
     builds its video, or drops it again. */
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    ref.current = el;
    setVideoEl(el);
  }, []);
  /* Whether this surface may play at all. A surface with a Pause of its own
     answers to it in place of the reduced-motion floor: its caller starts it
     paused under reduced motion, so it only ever moves because the viewer
     pressed it. Everything else answers to the floor and the wall's pause. */
  const mayPlay = viewerPaused === undefined ? allowed : !viewerPaused && !paused;
  const soundHere = plateId !== undefined && soundPlateId === plateId;
  /* A plate is "shown" once anything is actually painted on it: the poster, or
     the first video frame. Until then the surface wears the shimmering loading
     face, because a mirrored trend video is large and on a slow line the pane
     is otherwise just empty. The poster cannot be observed through the <video>
     element, so a detached probe loads the same URL and resolves from the same
     cache. */
  const [mediaShown, setMediaShown] = useState(false);
  /* The poster is the platform's own signed still (tiktokcdn, cdninstagram) and
     those links expire within hours, answering 403. A <video> whose poster fails
     paints nothing, and a wall plate at rest never plays, so a dead poster left a
     black pane. The probe below records the URL that failed, and the plate falls
     back to its own first frame. Keyed by URL so a fresh poster is trusted again. */
  const [deadPoster, setDeadPoster] = useState<string | null>(null);
  const usablePoster = poster !== null && poster !== deadPoster ? poster : null;
  /* Built only when it has something to do: asked to play (pointer, keyboard,
     stage) or heard. A plate whose poster is dead keeps it, because its first
     frame is the only picture it has. */
  const wantsMotion = mayPlay && (keepPlaying || active);
  const mountVideo = !restAsStill || usablePoster === null || wantsMotion || soundHere;

  useEffect(() => {
    if (!poster) return;
    const probe = document.createElement("img");
    /* Asked for the way the stills ask, with no referrer, and decoded off the
       main thread. Not lazy: it is never in the document, so a lazy probe would
       wait on a visibility it can never have, and the poster it checks is
       fetched by the <video> anyway. */
    probe.referrerPolicy = "no-referrer";
    probe.decoding = "async";
    probe.onload = () => {
      setMediaShown(true);
      /* The poster is the video's own still, so it carries the clip's shape and
         it arrives first: a plate with a healthy poster preloads nothing, and
         one that never plays never reaches loadedmetadata at all. A caller
         sizing its box to the media gets the answer from here and has it
         confirmed by the video below. */
      onNaturalSize?.(probe.naturalWidth, probe.naturalHeight);
    };
    probe.onerror = () => setDeadPoster(poster);
    probe.src = poster;
    return () => {
      probe.onload = null;
      probe.onerror = null;
    };
  }, [poster, onNaturalSize]);

  /* With no poster to show, `preload="metadata"` loads the clip but Chrome paints
     no frame until something seeks, so the plate seeks a hair past zero once the
     metadata is in. Only a paused plate still at zero: never one that is playing
     or that the viewer has moved.
     A poster that died after mount leaves an element that was created with
     `preload="none"` and has fetched nothing, and a browser need not start
     loading just because the attribute changed, so that one plate is asked to
     load. Only while it holds nothing and is not playing. */
  useEffect(() => {
    const el = ref.current;
    if (!el || usablePoster) return;
    if (poster !== null && deadPoster === poster && el.paused && el.readyState === HTMLMediaElement.HAVE_NOTHING) {
      el.load();
    }
    const paintFirstFrame = () => {
      if (el.paused && el.currentTime === 0) el.currentTime = 0.1;
    };
    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) paintFirstFrame();
    else el.addEventListener("loadedmetadata", paintFirstFrame, { once: true });
    return () => el.removeEventListener("loadedmetadata", paintFirstFrame);
  }, [videoEl, usablePoster, src, poster, deadPoster]);

  /* Without an observer to ask, a plate stays eligible from the first render:
     the playback gate above it is the one that actually has to hold. Read once
     at mount rather than assigned from the effect, so the initial state is
     already right instead of being corrected by a second render. */
  const [onScreen, setOnScreen] = useState(() => typeof IntersectionObserver === "undefined");

  /* A plate scrolled out of the field is not being watched, so it is not played.
     The margin buys a short pre-roll where the page scrolls with the window; the
     cockpit scrolls in its own overflow box, and an implicit-root observer is
     also clipped by that ancestor, so inside the shell a plate starts as it
     arrives. Strict either way, which is the direction that has to be right. */
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => setOnScreen(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: "150px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [videoEl]);

  /* Three gates, all of which have to be open, and the order they are written in
     is the order of authority: the reduced-motion floor and the viewer's own
     pause live in `allowed`, then `active` (the carousel's slide on the stage,
     or the wall card under the pointer or the keyboard), then whether the
     surface is on screen at all. An inactive slide is PAUSED, not merely
     scrolled out of sight, which is the difference between a carousel that plays
     one video and one that plays six behind a mask.
     The plate being heard skips the last two: the viewer asked to listen to it,
     and a voice that cut out when the pointer drifted off would be a control
     that undoes itself. `allowed` still holds it, and so does an open stage,
     which clears the sound plate upstream. */
  /* `src` is a dependency too: the element outlives a re-signed URL, and a new
     src resets it to paused, so a plate being looked at is started again. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (mayPlay && (keepPlaying || (active && onScreen))) {
      /* A rejected play() is a browser policy answer, not a broken mirror, so it
         must never flip the plate to its "nothing was captured" face. */
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [videoEl, mayPlay, active, onScreen, keepPlaying, src]);

  /* Muting is done to the ELEMENT, not through the attribute. React writes
     `muted` as a DOM property on mount and there is no attribute for it to
     reconcile against afterwards, so a re-render with a different value leaves a
     playing video exactly as loud as it already was. An effect is the only thing
     that actually flips it. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = !withSound;
  }, [videoEl, withSound]);

  /* A plate that leaves the wall takes its voice with it. Without this, the
     sound plate would still be named after a filter or a page change removed
     it, and the day it came back it would start playing with sound on its own,
     with no click behind it. Read through a ref so the release sees the last
     render's answer and runs on unmount only, not on every context change. */
  /* A heard plate that leaves the viewport ENTIRELY gives its sound up, and it
     does not come back on its own: scrolling past a voice is the same as
     turning it off. Strict, threshold 0 and no margin, so a plate still showing
     a sliver of itself keeps playing. Observed only while this is the plate
     being heard; every other plate's play gate is the observer above. */
  useEffect(() => {
    const el = ref.current;
    if (!soundHere || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.every((entry) => !entry.isIntersecting)) setSoundPlate(null);
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [videoEl, soundHere, setSoundPlate]);

  const releaseSound = useRef<() => void>(() => {});
  useEffect(() => {
    releaseSound.current = () => {
      if (soundHere) setSoundPlate(null);
    };
  });
  useEffect(() => () => releaseSound.current(), []);

  /* The chip is a control of its own sitting over the card's hit area, so the
     click stops here and never opens the detail. Unmuting happens INSIDE the
     handler: a browser only lets a page raise a video's volume in direct answer
     to a gesture, and the effect above runs a render later, outside it. Held
     still (the viewer's pause), the choice is recorded but nothing is unmuted:
     the effect gives it a voice when the wall plays again. */
  const onToggleSound = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (plateId === undefined) return;
    const el = ref.current;
    if (soundHere) {
      if (el) el.muted = true;
      setSoundPlate(null);
      return;
    }
    /* Started here too, inside the gesture, so a plate the pointer is not on
       begins playing with its sound at once rather than waiting for a render. */
    if (el && allowed) {
      el.muted = false;
      void el.play().catch(() => {});
    }
    setSoundPlate(plateId);
  };

  /* No autoPlay attribute anywhere: the effect above is the only thing that ever
     starts a plate, which is what keeps the reduced-motion floor real. */
  return (
    <>
      {/* The resting face of a wall plate: the poster the probe above already
          fetched, so it comes from cache. It stays under a video built over
          it, so swapping between the two never flashes the empty box. */}
      {restAsStill && usablePoster ? (
        <img
          src={usablePoster}
          alt=""
          referrerPolicy="no-referrer"
          decoding="async"
          draggable={false}
          className={cn("co-plate-surface", !mediaShown && "co-plate-loading")}
        />
      ) : null}
      {mountVideo ? (
        <video
          ref={attachVideo}
          src={src}
          poster={usablePoster ?? undefined}
          muted
          loop
          playsInline
          /* Nothing is fetched for a plate at rest while its poster is healthy:
             the poster is what shows, and a plate only plays under the pointer,
             the keyboard or on the stage, where play() loads it. Only the
             first-frame fallback, with no poster to show, needs the metadata. */
          preload={usablePoster ? "none" : "metadata"}
          onLoadedData={() => setMediaShown(true)}
          onLoadedMetadata={(event) =>
            onNaturalSize?.(event.currentTarget.videoWidth, event.currentTarget.videoHeight)
          }
          onError={onError}
          /* The skeleton is withheld when there is no poster AND nothing is about
             to play: preload="metadata" may never surface a frame there, and a
             shimmer that can never resolve is a worse answer than the quiet pane
             this state has always shown. A wall plate at rest is exactly that
             case now that plates only play under the pointer. */
          className={cn(
            "co-plate-surface",
            !mediaShown &&
              (usablePoster !== null || (mayPlay && (active || keepPlaying))) &&
              "co-plate-loading",
          )}
        />
      ) : null}
      {/* Absent, not disabled, under the reduced-motion floor: every plate is a
          still there, and a sound control over a still does nothing. A plate with
          no video never reaches this component, so it never draws one either. */}
      {plateId !== undefined && !reduced ? (
        <SoundChip on={soundHere} title={plateTitle ?? "this plate"} onToggle={onToggleSound} />
      ) : null}
    </>
  );
}

function PlateMediaBody({
  videoUrl,
  thumbnailUrl,
  standIn,
  scale,
  active,
  hint,
  viewerPaused,
  plateId,
  plateTitle,
  onNaturalSize,
}: {
  videoUrl: string | null;
  thumbnailUrl: string | null;
  standIn?: PlateArt;
  scale: PlateScale;
  active: boolean;
  hint?: string;
  viewerPaused?: boolean;
  plateId?: string;
  plateTitle?: string;
  onNaturalSize?: (width: number, height: number) => void;
}) {
  const { soundOn, soundPlateId, allowed, stageOpen } = usePlayback();
  /* The URL that failed, not a flag: this body outlives a refetch that
     re-signs its links, and a reissued link has to be trusted again. */
  const [failedVideo, setFailedVideo] = useState<string | null>(null);
  const [failedPoster, setFailedPoster] = useState<string | null>(null);
  const videoFailed = videoUrl !== null && failedVideo === videoUrl;
  const posterFailed = thumbnailUrl !== null && failedPoster === thumbnailUrl;
  /* A plate on the wall while a detail stage is open over it: held still,
     whatever the pointer is doing, so the stage is the only thing moving. */
  const heldForStage = scale === "plate" && stageOpen;
  /* The one wall plate the viewer chose to hear, and only while the wall is
     playing and no stage is open over it. */
  const heard =
    scale === "plate" &&
    plateId !== undefined &&
    soundPlateId === plateId &&
    allowed &&
    !stageOpen;

  if (videoUrl && !videoFailed) {
    return (
      <PlateVideo
        src={videoUrl}
        poster={thumbnailUrl}
        active={active && !heldForStage}
        /* Two ways to be heard, each one asked for. A detail stage: only the
           item actually on it, and only once the viewer turned its sound on. A
           wall plate: only the one plate the viewer chose, and never while the
           wall is held still, by their pause, by the reduced-motion floor or by
           an open stage. */
        withSound={scale === "stage" ? active && soundOn : heard}
        keepPlaying={heard}
        viewerPaused={viewerPaused}
        plateId={plateId}
        plateTitle={plateTitle}
        restAsStill={scale === "plate"}
        onError={() => setFailedVideo(videoUrl)}
        onNaturalSize={onNaturalSize}
      />
    );
  }

  if (thumbnailUrl && !posterFailed) {
    return (
      <Image
        src={thumbnailUrl}
        alt=""
        referrerPolicy="no-referrer"
        /* A still is only a picture of a plate at rest: fetched as it nears
           the viewport and decoded off the main thread. */
        loading="lazy"
        decoding="async"
        /* Only ever a real decode: Image swallows its own onLoad once a source
           has failed and swaps in the broken-image glyph, so the size reported
           here is always the picture's and never the placeholder's. */
        onLoad={(event) =>
          onNaturalSize?.(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)
        }
        onError={() => setFailedPoster(thumbnailUrl)}
        className="co-plate-surface"
      />
    );
  }

  // No URL survived. In the harness a drawn stand-in takes over; anywhere else
  // there is genuinely nothing to show and the honest plate is the only answer.
  if (standIn) {
    return <div className={cn("co-plate-surface co-plate-art", artClass(standIn))} />;
  }
  return nothingCaptured(scale, hint);
}

/**
 * The one media resolver every wall and panel goes through.
 *
 * The body is keyed on WHAT it shows, not on its links. Every refetch mints
 * fresh presigned URLs, and a body keyed on them rebuilt every <video> on the
 * wall each time: a caller that names the item (`mediaId`) keeps its element,
 * and the new src is simply applied to it. The failure flags record the URL
 * that failed, so a link that has since been reissued is trusted again either
 * way. A caller that names nothing is keyed on the URLs, as before.
 */
export function PlateMedia({
  videoUrl,
  videoBroken = false,
  thumbnailUrl,
  standIn,
  scale = "plate",
  active = true,
  hint,
  mediaId,
  viewerPaused,
  plateId,
  plateTitle,
  onNaturalSize,
}: {
  videoUrl: string | null;
  /** Optional, and false when it is not passed: only Trends stores a flag for a
   *  mirror it already knows is dead (TrendPreviewVideo.videoBroken). A
   *  Reference carries no such field, so a caller that has nothing to say about
   *  the mirror says nothing and the URLs decide, exactly as they did before
   *  this prop existed for anyone. */
  videoBroken?: boolean;
  thumbnailUrl: string | null;
  standIn?: PlateArt;
  scale?: PlateScale;
  /** Defaults to true, so every surface that is simply looked at where it sits
   *  keeps the behaviour it had. A carousel passes false for the slides that
   *  are not the one on the stage, and the Trends wall passes whether its card
   *  is under the pointer or the keyboard, which is when a wall plate plays. */
  active?: boolean;
  /** The second line of the nothing-was-captured face. The caller owns it: this
   *  box cannot know what opening the item reveals, and a shared primitive that
   *  hardcoded one surface's sentence would print it on the other. */
  hint?: string;
  /** The stable identity of what this surface shows (a trend's id, an
   *  example's id). Passed, a re-signed URL updates the element in place
   *  instead of remounting it. */
  mediaId?: string;
  /** The viewer's own Pause, from a surface that draws one: the detail stage
   *  (WCAG 2.2.2). Passed, true holds the video and false lets it play, and it
   *  stands in for the reduced-motion floor on this surface, which is why the
   *  caller must start it true when reduced motion is asked for. The wall's own
   *  pause still holds it. Omitted, the floor governs, as everywhere else. */
  viewerPaused?: boolean;
  /** A wall plate's own id. Passed, it gives a playing video a sound chip and
   *  makes this the plate the viewer can choose to hear; the playback context
   *  holds which one that is, so choosing it mutes every other plate. A stage
   *  and a thumbnail pass nothing and never carry a chip. */
  plateId?: string;
  /** The name the sound chip says it is for: "Sound for {title}". */
  plateTitle?: string;
  /** The intrinsic size of whatever actually painted, in pixels, the moment it
   *  is known — the poster's, then the video's, or the still's.
   *
   *  It fires ONLY for real media. The honest hatch and the drawn stand-ins have
   *  no intrinsic size and report none, so a caller sizing its box to the media
   *  can tell "this is 9:16" from "we do not know what shape this was", which is
   *  the difference between fitting a frame and inventing one. Pass a stable
   *  callback: the poster probe's effect depends on it.  */
  onNaturalSize?: (width: number, height: number) => void;
}) {
  if (videoBroken) return nothingCaptured(scale, hint);
  return (
    <PlateMediaBody
      key={mediaId ?? `${videoUrl ?? ""}|${thumbnailUrl ?? ""}`}
      videoUrl={videoUrl}
      thumbnailUrl={thumbnailUrl}
      standIn={standIn}
      scale={scale}
      active={active}
      hint={hint}
      viewerPaused={viewerPaused}
      plateId={plateId}
      plateTitle={plateTitle}
      onNaturalSize={onNaturalSize}
    />
  );
}
